import { motion } from 'framer-motion';

export default function Loader() {
  const paths = [
    "M403.23,31.35v6.91q-.21,301.47-.41,602.94c0,14.67.28,29.35.5,44,0,2.62-.41,4.66-2.63,6.56-15.33,13.17-30.48,26.54-45.76,39.76-11.09,9.6-22.29,19.06-33.47,28.56-1,.88-2.27,1.53-4.21,2.82-.1-2.51-.24-4.21-.23-5.91q0-26.84.06-53.68.24-169.4.46-338.79c0-70.34-.22-140.67-.32-211,0-5.37.19-10.74.27-16.11.22-13.75,4.55-26.13,13.45-36.54,8.12-9.51,16.8-18.56,25.42-27.63,13.2-13.87,26.66-27.49,41.72-39.41,1.34-1.07,2.7-2.12,4.06-3.17Z",
    "M191.82,30.42C198.37,36.71,205,43,211.46,49.29q18.25,17.77,36.43,35.65Q253,90,257.76,95.53c10.11,11.79,16.05,25.29,16.46,41,.16,6.08.44,12.16.44,18.24q-.11,236.43-.22,472.88c0,42.4.58,84.8.87,127.2,0,2.43-.13,4.85-.23,8.44-2.33-1.81-3.75-2.79-5-3.92-15.57-13.7-31.05-27.51-46.71-41.12q-15.17-13.2-30.78-25.9c-2.52-2.06-3.29-4.23-3.27-7.35.46-84.8,1.05-169.61,1.19-254.41q.3-190.83.06-381.64V30.79Z",
    "M56.55,139c2.71,2.27,5.53,4.41,8.1,6.83Q89,168.86,113.32,192c7.16,6.81,13.59,14.18,18.52,22.84a61.69,61.69,0,0,1,8.57,29.56c.1,4.11.21,8.22.23,12.34q.72,139,1.36,278c.1,31.84-.53,63.69-.85,95.53,0,1.75-.21,3.5-.39,6.41A62.22,62.22,0,0,1,135.6,633c-15.68-13.85-31.44-27.61-46.93-41.67-10.19-9.24-20.33-18.56-29.06-29.3a20.08,20.08,0,0,1-4.83-13.65q.44-61.18.5-122.37,0-137.42-.3-274.82V139.53Z",
    "M536.86,139v9.59q-.24,161.83-.44,323.65c0,25.94.37,51.88.73,77.82.07,4.68-.58,8.93-3.73,12.49-4.13,4.68-8,9.63-12.64,13.81C507,588.82,492.93,601,479,613.31c-6,5.34-11.87,10.87-17.91,16.19-2.77,2.45-5.8,4.6-8.81,7-2.11-2.3-1.45-4.55-1.45-6.55,0-25.94.11-51.89.12-77.83q0-131.22.07-262.46c0-15.56.39-31.12.57-46.68.1-9.2,2.94-17.49,7.66-25.35,6.89-11.47,16-21,25.44-30.32,10.85-10.63,21.6-21.38,32.45-32,4.59-4.5,9.28-8.91,14-13.3,1.4-1.3,3-2.35,4.53-3.52Z"
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background overflow-hidden">
      {/* Background architectural grid */}
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'linear-gradient(#A69080 1px, transparent 1px), linear-gradient(90deg, #A69080 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="relative">
        <svg viewBox="0 0 591.94 793.7" className="w-40 h-auto overflow-visible">
          {paths.map((d, i) => (
            <motion.path
              key={i}
              d={d}
              className="fill-secondary stroke-primary"
              strokeWidth="2"
              initial={{ pathLength: 0, fillOpacity: 0 }}
              animate={{
                pathLength: [0, 1, 1],
                fillOpacity: [0, 0, 1],
                y: [20, 0, 0]
              }}
              transition={{
                duration: 1,
                delay: i * 0.3,
                times: [0, 0.5, 1],
                ease: "easeInOut"
              }}
            />
          ))}
        </svg>

        {/* Floating dust/light particles for atmosphere */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            animate={{
              y: [0, -100],
              opacity: [0, 1, 0],
              x: Math.random() * 100 - 50
            }}
            transition={{
              duration: Math.random() * 2 + 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-12 text-center"
      >
        <h2 className="text-primary font-serif text-xl tracking-[0.3em] uppercase italic">
          {Array.from("Building Your Vision").map((char, index) => (
            <motion.span
              key={index}
              animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.05, 1] }}
              transition={{
                repeat: Infinity,
                duration: 2,
                delay: index * 0.1,
                ease: "easeInOut"
              }}
              style={{ display: "inline-block" }}
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </h2>
        <div className="mt-4 flex justify-center gap-1">
          {[0, 1, 2].map((dot) => (
            <motion.div
              key={dot}
              className="w-1.5 h-1.5 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3] }}
              transition={{ repeat: Infinity, duration: 1, delay: dot * 0.2 }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
